import { useEventCallback } from "@stenajs-webui/core";
import { Select, type SelectProps } from "@stenajs-webui/select";
import type { FilterOptionOption } from "react-select";

import { baseFilterOption } from "../../common/react-select/filter";
import type { DropdownOption } from "../../common/utils/optionTransformers";
import { zIndices } from "../../common/zIndices";
import { getPortalRoot } from "../../use-cases/manage/grid/utils/getPortalRoot";
import { useDropdownMenuTargetPortal } from "./DropdownMenuPortalTargetContext";

const portalRoot = getPortalRoot();

export interface GroupedOption<TOption> {
  label: string;
  options: TOption[];
  value: string;
}

export type GroupedOptionsType<TOption> = ReadonlyArray<GroupedOption<TOption>>;

export interface DropdownGridProps<TValue> extends SelectProps<
  DropdownOption<TValue>
> {
  height: number;
}

export function DropdownGrid<TValue>({
  filterOption,
  ...props
}: DropdownGridProps<TValue>) {
  const _filterOption = useEventCallback(
    (option: FilterOptionOption<DropdownOption<TValue>>, rawInput: string) =>
      (filterOption?.(option, rawInput) ?? true) &&
      baseFilterOption(option, rawInput),
  );

  return (
    <Select<DropdownOption<TValue>>
      filterOption={_filterOption}
      menuPortalTarget={portalRoot}
      styles={{
        control: (base) => ({
          ...base,
          border: "none",
          boxShadow: "none",
          borderRadius: "0",
          flexWrap: "nowrap",
          height: `${props.height - 2}px`,
          minHeight: `${props.height - 2}px`,
          "&:hover": {
            border: "none",
          },
          lineHeight: "20px",
        }),
        valueContainer: (base) => ({
          ...base,
          padding: "0 2px",
        }),

        menuPortal: (base) => {
          return {
            ...base,
            display: "inline-block",
            zIndex: zIndices.dropdown,
            left:
              typeof base.left === "number"
                ? (base.left as number) +
                  (window.pageXOffset || document.documentElement.scrollLeft)
                : base.left,
          };
        },
      }}
      {...props}
    />
  );
}

export interface DropdownProps<TValue> extends SelectProps<
  DropdownOption<TValue>
> {
  isEdited?: boolean;
  isError?: boolean;
  width?: number;
}

export function Dropdown<TValue>({
  isEdited = false,
  isError = false,
  width,
  filterOption,
  ...props
}: DropdownProps<TValue>) {
  const _filterOption = useEventCallback(
    (option: FilterOptionOption<DropdownOption<TValue>>, rawInput: string) =>
      (filterOption?.(option, rawInput) ?? true) &&
      baseFilterOption(option, rawInput),
  );

  const menuPortalTarget = useDropdownMenuTargetPortal();

  return (
    <Select
      filterOption={_filterOption}
      menuPortalTarget={menuPortalTarget}
      menuPlacement={"auto"}
      menuShouldScrollIntoView={false}
      variant={isError ? "error" : undefined}
      styles={{
        singleValue: (base) => ({
          ...base,
          width: "100%",
        }),
        container: (base) => ({ ...base, width }),
        control: (base) =>
          isEdited
            ? {
                ...base,
                maxHeight: 36,
                flexWrap: "nowrap",
                fontStyle: "italic",
                fontWeight: "bold",
              }
            : {
                ...base,
                maxHeight: 36,
                flexWrap: "nowrap",
              },
        menu: (base) => ({
          ...base,
          zIndex: zIndices.dropdown,
        }),
        menuPortal: (base) => ({
          ...base,
          display: "inline-block",
          zIndex: zIndices.dropdown,
          left:
            typeof base.left === "number"
              ? (base.left as number) +
                (window.pageXOffset || document.documentElement.scrollLeft)
              : base.left,
        }),
      }}
      {...props}
    />
  );
}
